import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { supabase } from "@/integrations/supabase/client";
import Logo from "@/components/Logo";
import {
  Check,
  Mic,
  LayoutGrid,
  CalendarDays,
  Route,
  Sparkles,
  FileSpreadsheet,
  Compass,
  ArrowRight,
  Download,
} from "lucide-react";


/* ---------- Shared presentation primitives ---------- */

const Label = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-[#E9A04A]">
    {children}
  </span>
);

const Chip = ({ children, count }: { children: React.ReactNode; count?: string }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#17171a] px-4 py-2 text-sm text-[#cfcac2]">
    {children}
    {count && <span className="text-xs text-[#7d786f]">{count}</span>}
  </span>
);

const Tile = ({
  className = "",
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={`relative overflow-hidden rounded-[26px] border border-white/[0.07] bg-[#161618] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-white/20 ${className}`}
  >
    {children}
  </div>
);

const Bullet = ({ children }: { children: React.ReactNode }) => (
  <li className="flex items-start gap-2.5 text-sm text-[#a5a099]">
    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#E9A04A]" />
    <span>{children}</span>
  </li>
);

const SectionHead = ({
  num,
  label,
  title,
  children,
}: {
  num: string;
  label: string;
  title: string;
  children?: React.ReactNode;
}) => (
  <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div className="max-w-2xl">
      <Label>{label}</Label>
      <h2 className="mt-3 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-[#EDEAE4] md:text-5xl">
        {title}
      </h2>
      {children && <p className="mt-4 text-base leading-relaxed text-[#8f8a83]">{children}</p>}
    </div>
    <span className="hidden select-none font-heading text-7xl font-bold leading-none text-white/[0.05] md:block">
      {num}
    </span>
  </div>
);

/* ---------- Page ---------- */

const Landing = () => {
  usePageSeo({
    path: "/",
    title: "Salesby — AI Sales CRM for Field & B2B Sales Reps",
    description:
      "Salesby is the AI-powered CRM for outside sales and manufacturers' reps: pipeline, route mapping, follow-ups, trade shows, and AI contact import.",
    keywords:
      "sales CRM, field sales app, B2B CRM, manufacturers rep CRM, AI sales assistant",
  });

  const navigate = useNavigate();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const [help, setHelp] = useState<string | null>(null);

  useEffect(() => {
    const isPWA =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    if (!isPWA) return;

    let cancelled = false;
    const routeInstalledApp = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;
      navigate(session ? "/index" : "/login", { replace: true });
    };
    void routeInstalledApp();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const installHint = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    if (isIOS)
      setHelp(
        "iPhone: 1) Tap the Share button in Safari 2) Scroll to 'Add to Home Screen' 3) Tap 'Add'"
      );
    else if (isAndroid)
      setHelp("Android: Tap the 3-dot menu (⋮) → 'Install app' or 'Add to Home Screen'");
    else setHelp("Desktop: Click the Install icon in your browser's address bar");
    window.setTimeout(() => setHelp(null), 10000);
  };

  const handleInstall = async () => {
    const ok = await promptInstall();
    if (!ok) installHint();
  };

  return (
    <div className="min-h-screen bg-[#0F0F10] font-body text-[#EDEAE4] antialiased selection:bg-[#E9A04A] selection:text-[#0F0F10]">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#0F0F10]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="rounded-full bg-white/95 px-3 py-1">
            <Logo />
          </div>
          <nav className="flex items-center gap-1 sm:gap-3">
            <a
              href="/sales-guide"
              className="hidden rounded-full px-4 py-2 text-sm text-[#a5a099] transition-colors hover:text-[#EDEAE4] sm:inline-block"
            >
              Sales Guide
            </a>
            <a
              href="/privacy-policy"
              className="hidden rounded-full px-4 py-2 text-sm text-[#a5a099] transition-colors hover:text-[#EDEAE4] md:inline-block"
            >
              Privacy
            </a>
            <button
              onClick={() => navigate("/login")}
              className="rounded-full px-4 py-2 text-sm text-[#a5a099] transition-colors hover:text-[#EDEAE4]"
            >
              Log in
            </button>
            <button
              onClick={() => navigate("/register")}
              className="rounded-full bg-[#E9A04A] px-5 py-2 text-sm font-semibold text-[#0F0F10] transition-transform hover:scale-[1.03]"
            >
              Start free
            </button>
          </nav>
        </div>
      </header>

      {help && !isInstalled && (
        <div className="fixed bottom-6 left-4 right-4 z-50 mx-auto max-w-md rounded-2xl border border-white/10 bg-[#1a1a1c] px-4 py-3 text-sm text-[#cfcac2] shadow-2xl sm:left-auto sm:right-6">
          {help}
        </div>
      )}

      <main role="main">
        {/* ── HERO ───────────────────────────── */}
        <section className="relative overflow-hidden px-5 pt-16 pb-14">
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-[-14rem] h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-[#E9A04A]/[0.13] blur-[140px]"
          />
          <div className="relative mx-auto max-w-6xl">
            <Label>The sales operating system</Label>
            <h1 className="mt-4 max-w-4xl font-heading text-[3.1rem] font-bold leading-[0.98] tracking-[-0.03em] text-[#EDEAE4] sm:text-7xl">
              Everything your day
              <br />
              needs, in one screen.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#8f8a83]">
              <span className="text-[#E9A04A]">7 live panels</span> · pipeline, follow-ups,
              routes and AI capture — built for outside sales reps who move fast and hate
              clicking around.
            </p>

            {/* CTA bar */}
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                onClick={() => navigate("/register")}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#E9A04A] px-7 py-4 text-base font-semibold text-[#0F0F10] shadow-[0_10px_40px_-10px_rgba(233,160,74,0.6)] transition-transform hover:scale-[1.02]"
              >
                Start 7-day free trial
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate("/sales-guide")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-[#17171a] px-7 py-4 text-base font-medium text-[#cfcac2] transition-colors hover:border-white/25"
              >
                Learn how to sell in the USA
              </button>
            </div>

            {/* Pricing chips */}
            <div className="mt-8 flex flex-wrap gap-2.5">
              <button
                onClick={() => navigate("/register")}
                className="rounded-full border border-white/10 bg-[#17171a] px-5 py-2.5 text-sm text-[#cfcac2] transition-colors hover:border-[#E9A04A]/60"
              >
                Individual <span className="ml-1.5 font-semibold text-[#EDEAE4]">$99/mo</span>
              </button>
              <button
                onClick={() => navigate("/register?type=corporation")}
                className="rounded-full border border-white/10 bg-[#17171a] px-5 py-2.5 text-sm text-[#cfcac2] transition-colors hover:border-[#E9A04A]/60"
              >
                Corporation <span className="ml-1.5 font-semibold text-[#EDEAE4]">$299/mo</span>
              </button>
              <Chip>7-day free trial</Chip>
              <Chip>Cancel anytime</Chip>
              {(isInstallable || !isInstalled) && (
                <button
                  onClick={handleInstall}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#17171a] px-5 py-2.5 text-sm text-[#cfcac2] transition-colors hover:border-[#E9A04A]/60"
                >
                  <Download className="h-3.5 w-3.5" /> Install app
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ── DASHBOARD BENTO ────────────────── */}
        <section className="px-5 py-16">
          <div className="mx-auto max-w-6xl">
            <SectionHead num="01" label="The dashboard" title="One screen. One glance. Full control.">
              Industries, clients, client info, follow-ups, weekly calendar, reminders and
              activities — all working together without a single extra click.
            </SectionHead>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-6 md:items-start">

              {/* Big feature tile */}
              <Tile className="md:col-span-4 md:row-span-2">
                <Label>Live command center</Label>
                <h3 className="mt-3 font-heading text-2xl font-bold text-[#EDEAE4]">
                  Follow Up Panel
                </h3>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-[#8f8a83]">
                  Capture every interaction and move clients through pipeline stages — all in
                  one place, synced everywhere instantly.
                </p>
                <ul className="mt-5 space-y-2">
                  <Bullet>Every call, email and visit logged in seconds</Bullet>
                  <Bullet>Next-step dates drive your reminders automatically</Bullet>
                  <Bullet>Client info, history and stage — always one click away</Bullet>
                </ul>
              </Tile>

              {/* Amber highlight tile */}
              <Tile className="md:col-span-2 border-transparent bg-[#E9A04A] hover:border-transparent">
                <LayoutGrid className="h-6 w-6 text-[#0F0F10]/70" />
                <h3 className="mt-4 font-heading text-2xl font-bold leading-tight text-[#0F0F10]">
                  7 panels,
                  <br />
                  zero tab-hopping.
                </h3>
                <p className="mt-2 text-sm text-[#0F0F10]/70">
                  Industries · Clients · Client Info · Follow-ups · Calendar · Reminds ·
                  Activities
                </p>
              </Tile>

              <Tile className="md:col-span-2">
                <CalendarDays className="h-5 w-5 text-[#E9A04A]" />
                <h3 className="mt-4 font-heading text-lg font-bold text-[#EDEAE4]">
                  Weekly Calendar
                </h3>
                <p className="mt-1.5 text-sm text-[#8f8a83]">
                  Color-coded week view with reminders for every next step.
                </p>
              </Tile>

              <Tile className="md:col-span-2">
                <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Industries</h3>
                <p className="mt-1.5 text-sm text-[#8f8a83]">Filter your book instantly.</p>
                <div className="mt-4 flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                  <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                </div>
              </Tile>

              <Tile className="md:col-span-2">
                <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Clients</h3>
                <p className="mt-1.5 text-sm text-[#8f8a83]">
                  Every client by industry, ranked by priority color.
                </p>
              </Tile>

              <Tile className="md:col-span-2">
                <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Activities</h3>
                <p className="mt-1.5 text-sm text-[#8f8a83]">
                  Fast notes about what happened this week.
                </p>
              </Tile>
            </div>
          </div>
        </section>

        {/* ── PIPELINE + WEEKLY LOG ──────────── */}
        <section className="px-5 py-16">
          <div className="mx-auto max-w-6xl">
            <SectionHead num="02" label="Momentum" title="Stages that move, history that proves it.">
              The Pipeline shows where every client stands. The Weekly Log turns your week into
              a clean, professional timeline you can copy straight into a report.
            </SectionHead>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-5 md:items-start">
              <Tile className="md:col-span-3">
                <Label>Pipeline</Label>
                <h3 className="mt-3 font-heading text-2xl font-bold text-[#EDEAE4]">
                  Intro → NDA → RFQ, in real time
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#8f8a83]">
                  Drag and drop between stages or change them from the Follow Up header.
                  Everything stays synchronized — and the stages are yours to define.
                </p>
                <ul className="mt-5 space-y-2">
                  <Bullet>Drag and drop between stages</Bullet>
                  <Bullet>Real-time synchronization</Bullet>
                  <Bullet>Fully custom stage creation</Bullet>
                </ul>
              </Tile>

              <div className="grid gap-4 md:col-span-2">
                <Tile>
                  <Label>Weekly log</Label>
                  <h3 className="mt-3 font-heading text-xl font-bold text-[#EDEAE4]">
                    Your week, already written
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#8f8a83]">
                    Every action and follow-up appears in order. Repeated contacts are grouped
                    and numbered — one click copies the whole week as plain text.
                  </p>
                  <ul className="mt-4 space-y-2">
                    <Bullet>Organized week by week</Bullet>
                    <Bullet>Repeat contacts grouped</Bullet>
                    <Bullet>One-click copy for reports</Bullet>
                  </ul>
                </Tile>
              </div>
            </div>
          </div>
        </section>

        {/* ── TRADE SHOWS / MELISSA ──────────── */}
        <section className="px-5 py-16">
          <div className="mx-auto max-w-6xl">
            <SectionHead num="03" label="Trade shows" title="Hands full? Just talk.">
              Built for the visitor walking the floor with catalogs in one hand and a phone in
              the other.
            </SectionHead>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-6 md:items-start">
              <Tile className="md:col-span-3 border-transparent bg-[#E9A04A] hover:border-transparent">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0F0F10]/15">
                  <Mic className="h-5 w-5 text-[#0F0F10]" />
                </div>
                <h3 className="mt-5 font-heading text-3xl font-bold leading-tight text-[#0F0F10]">
                  Meet Melissa,
                  <br />
                  your voice AI assistant.
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-[#0F0F10]/75">
                  Tap the microphone. Melissa asks which company you visited and what to save,
                  then updates your trade show panel instantly while you keep walking.
                </p>
              </Tile>

              <Tile className="md:col-span-3">
                <Label>Voice capture</Label>
                <h3 className="mt-3 font-heading text-2xl font-bold text-[#EDEAE4]">
                  Say it once. It's saved.
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#8f8a83]">
                  Melissa transcribes, detects the company, and files the note under the right
                  trade show automatically — no typing on the show floor.
                </p>
                <ul className="mt-5 space-y-2">
                  <Bullet>Automatic transcription</Bullet>
                  <Bullet>Company detection &amp; matching</Bullet>
                  <Bullet>Instant panel updates</Bullet>
                </ul>
              </Tile>

              <Tile className="md:col-span-2">
                <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Exhibitor ready</h3>
                <p className="mt-1.5 text-sm text-[#8f8a83]">
                  Capture booth conversations the moment they happen.
                </p>
              </Tile>
              <Tile className="md:col-span-2">
                <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Finish later</h3>
                <p className="mt-1.5 text-sm text-[#8f8a83]">
                  Complete details after the show, at your desk.
                </p>
              </Tile>
              <Tile className="md:col-span-2">
                <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Promote fast</h3>
                <p className="mt-1.5 text-sm text-[#8f8a83]">
                  Move contacts into Prospecting or the Dashboard.
                </p>
              </Tile>
            </div>
          </div>
        </section>

        {/* ── CONTACTS IMPORT ────────────────── */}
        <section className="px-5 py-16">
          <div className="mx-auto max-w-6xl">
            <SectionHead num="04" label="Contacts import" title="Drop a file. Get a database.">
              Excel, PDF, Word documents and images — including pages with multiple business
              cards. No scanner. No photo-by-photo.
            </SectionHead>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-6 md:items-start">
              <Tile className="md:col-span-4">
                <Label>From file to contacts</Label>
                <h3 className="mt-3 font-heading text-2xl font-bold text-[#EDEAE4]">
                  Hundreds of contacts in seconds
                </h3>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-[#8f8a83]">
                  Upload spreadsheets, documents or photos of business cards. Salesby extracts
                  names, titles, phones and emails, detects duplicates, and files everything
                  into organized folders ready to review.
                </p>
              </Tile>

              <Tile className="md:col-span-2">
                <FileSpreadsheet className="h-5 w-5 text-[#E9A04A]" />
                <h3 className="mt-4 font-heading text-lg font-bold text-[#EDEAE4]">
                  Multi-format import
                </h3>
                <ul className="mt-3 space-y-2">
                  <Bullet>Excel, PDF, Word and text</Bullet>
                  <Bullet>Automatic field mapping</Bullet>
                  <Bullet>Duplicate detection built in</Bullet>
                </ul>
              </Tile>

              <Tile className="md:col-span-3">
                <Sparkles className="h-5 w-5 text-[#E9A04A]" />
                <h3 className="mt-4 font-heading text-lg font-bold text-[#EDEAE4]">
                  AI image extraction
                </h3>
                <ul className="mt-3 space-y-2">
                  <Bullet>Multiple cards per image — no scanner needed</Bullet>
                  <Bullet>Front &amp; back auto-merge</Bullet>
                  <Bullet>Batch industry assignment</Bullet>
                </ul>
              </Tile>

              <Tile className="md:col-span-3">
                <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">
                  Folder organization
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#8f8a83]">
                  Every import lands in a named folder. Review in the Imports tab, assign
                  industries in batch, then promote the best contacts into your workflow.
                </p>
              </Tile>
            </div>
          </div>
        </section>

        {/* ── PLANNING & MAPPING ─────────────── */}
        <section className="px-5 py-16">
          <div className="mx-auto max-w-6xl">
            <SectionHead num="05" label="New · Planning & mapping" title="Plan the day. Drive the route.">
              Two tools built for sales reps in motion.
            </SectionHead>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Tile>
                <Compass className="h-5 w-5 text-[#E9A04A]" />
                <h3 className="mt-4 font-heading text-2xl font-bold text-[#EDEAE4]">
                  Daily Planning Organogram
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#8f8a83]">
                  Your planned route in blue, your actual execution trace in red — side by side.
                  Spot gaps instantly and stay accountable.
                </p>
                <ul className="mt-5 space-y-2">
                  <Bullet>Drag-and-drop activity blocks</Bullet>
                  <Bullet>Planned vs executed comparison</Bullet>
                  <Bullet>Pinch-zoom fullscreen on mobile</Bullet>
                  <Bullet>Notes attached to each step</Bullet>
                </ul>
              </Tile>

              <Tile>
                <Route className="h-5 w-5 text-[#E9A04A]" />
                <h3 className="mt-4 font-heading text-2xl font-bold text-[#EDEAE4]">
                  Sales Route Mapping
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#8f8a83]">
                  Pin clients and prospects on an interactive map, save trips, and plan
                  territory visits the smart way.
                </p>
                <ul className="mt-5 space-y-2">
                  <Bullet>Map all clients &amp; prospects</Bullet>
                  <Bullet>Saved trips &amp; territory routes</Bullet>
                  <Bullet>Custom labels &amp; pin colors</Bullet>
                  <Bullet>Built for outside &amp; field sales</Bullet>
                </ul>
              </Tile>

              <Tile className="md:col-span-2">
                <Label>Prospecting</Label>
                <h3 className="mt-3 font-heading text-xl font-bold text-[#EDEAE4]">
                  Where new ideas and leads begin
                </h3>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#8f8a83]">
                  For companies with interest but no contact yet. Early research stays separate
                  from your daily routine and industries never get mixed. When the opportunity
                  becomes real, move it into the Dashboard.
                </p>
              </Tile>
            </div>
          </div>
        </section>

        {/* ── PRICING ────────────────────────── */}
        <section className="px-5 py-16">
          <div className="mx-auto max-w-6xl">
            <SectionHead num="06" label="Pricing" title="Two plans. Everything included." />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Tile>
                <Label>Individual</Label>
                <p className="mt-4 font-heading text-5xl font-bold text-[#EDEAE4]">
                  $99
                  <span className="text-base font-medium text-[#7d786f]">/mo</span>
                </p>
                <p className="mt-2 text-sm text-[#8f8a83]">1 user · 7-day free trial</p>
                <ul className="mt-5 space-y-2">
                  <Bullet>Full dashboard, pipeline and weekly log</Bullet>
                  <Bullet>AI contact import and Melissa voice capture</Bullet>
                  <Bullet>Planning, mapping and prospecting</Bullet>
                </ul>
                <Button
                  size="lg"
                  onClick={() => navigate("/register")}
                  className="mt-7 w-full rounded-full bg-[#17171a] text-[#EDEAE4] hover:bg-[#202024]"
                >
                  Start free trial
                </Button>
              </Tile>

              <Tile className="border-transparent bg-[#E9A04A] hover:border-transparent">
                <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-[#0F0F10]/60">
                  Corporation · Team
                </span>
                <p className="mt-4 font-heading text-5xl font-bold text-[#0F0F10]">
                  $299
                  <span className="text-base font-medium text-[#0F0F10]/60">/mo</span>
                </p>
                <p className="mt-2 text-sm text-[#0F0F10]/70">Up to 5 users · 7-day free trial</p>
                <ul className="mt-5 space-y-2">
                  {[
                    "Everything in Individual",
                    "Multi-user team access",
                    "Centralized administration",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-[#0F0F10]/80">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F0F10]" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="lg"
                  onClick={() => navigate("/register?type=corporation")}
                  className="mt-7 w-full rounded-full bg-[#0F0F10] text-[#EDEAE4] hover:bg-[#0F0F10]/90"
                >
                  Start free trial
                </Button>
              </Tile>
            </div>

            <p className="mt-6 text-center text-sm text-[#7d786f]">
              By signing up you agree to our{" "}
              <a href="/privacy-policy" className="text-[#E9A04A] underline underline-offset-4">
                Privacy Policy
              </a>
            </p>
          </div>
        </section>

        {/* ── FOOTER ─────────────────────────── */}
        <footer className="mt-8 border-t border-white/[0.07] px-5 py-14">
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-3">
            <div>
              <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Need help?</h3>
              <p className="mt-2 text-sm text-[#8f8a83]">Contact our support team</p>
              <a
                href="mailto:support@salesby.us"
                className="mt-2 inline-block text-sm font-medium text-[#E9A04A] hover:underline"
              >
                support@salesby.us
              </a>
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Quick links</h3>
              <nav className="mt-2 flex flex-col gap-1.5 text-sm text-[#8f8a83]">
                <a href="/privacy-policy" className="hover:text-[#EDEAE4]">Privacy Policy</a>
                <a href="/register" className="hover:text-[#EDEAE4]">Start free trial</a>
                <a href="/sales-guide" className="hover:text-[#EDEAE4]">Free sales course</a>
              </nav>
            </div>
            <div className="md:text-right">
              <h3 className="font-heading text-lg font-bold text-[#EDEAE4]">Salesby CRM</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#8f8a83]">
                Professional sales management
                <br />
                for real business growth
              </p>
            </div>
          </div>
          <div className="mx-auto mt-10 max-w-6xl border-t border-white/[0.07] pt-6 text-center text-xs text-[#6d6862]">
            © {new Date().getFullYear()} Salesby. All rights reserved.
          </div>
        </footer>
      </main>

      {isInstalled && (
        <div className="fixed bottom-6 right-6 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-[#E9A04A] shadow-lg">
          <Check className="h-5 w-5 text-[#0F0F10]" />
        </div>
      )}
    </div>
  );
};

export default Landing;
